/**
 * Level-of-Detail band model (Macroblock #17, Phase B) — PURE, deterministic.
 *
 * Three explicit visual bands drive what the Grid renders at a given camera zoom:
 *
 *   OVERVIEW      zoom <= OVERVIEW_MAX_ZOOM          Grid geography + LOD0 brand
 *                                                    image. No Canvas, no Artwork
 *                                                    pixels. (Existing #12/LOD0
 *                                                    behaviour — UNCHANGED.)
 *   INTERMEDIATE  OVERVIEW_MAX_ZOOM < zoom <=        Lightweight, authoritative
 *                 DETAIL_MIN_ZOOM                     content preview (territory
 *                                                    has real content) — no full
 *                                                    Canvas/Artwork pixel load.
 *   DETAIL        zoom > DETAIL_MIN_ZOOM             Full published Canvas +
 *                                                    Frozen Artwork content.
 *
 * OVERVIEW_MAX_ZOOM is the FROZEN Claudio law (LOD0_VISIBILITY_MAX_ZOOM = 4.27)
 * and is reused verbatim — never redefined here. DETAIL_MIN_ZOOM is the NEW #17
 * threshold introduced to carve out the intermediate band; it is intentionally a
 * single named constant so it can be tuned at Visual Checkpoint 1 without hunting
 * magic literals.
 */
import { LOD0_VISIBILITY_MAX_ZOOM } from "./lod0-brand-image";

export type LodBand = "overview" | "intermediate" | "detail";

/** FROZEN overview cutoff — the LOD0 brand image visibility law (Claudio). */
export const OVERVIEW_MAX_ZOOM = LOD0_VISIBILITY_MAX_ZOOM; // 4.27

/**
 * #17 detail floor (Claudio-tunable at Visual Checkpoint 1). Full Canvas/Artwork
 * detail renders only at/above this zoom; between the overview cutoff and here we
 * show the lightweight INTERMEDIATE representation. Camera MAX_ZOOM is 10.
 */
export const DETAIL_MIN_ZOOM = 7.0;

/** Deterministic band for a camera zoom. Boundaries: overview inclusive at the
 * overview cutoff (matches the LOD0 hard cutoff); detail strictly above the
 * detail floor. */
export function lodBandForZoom(zoom: number): LodBand {
  if (!Number.isFinite(zoom)) return "overview";
  if (zoom <= OVERVIEW_MAX_ZOOM) return "overview";
  if (zoom <= DETAIL_MIN_ZOOM) return "intermediate";
  return "detail";
}

export function isDetailBand(zoom: number): boolean {
  return lodBandForZoom(zoom) === "detail";
}

export function isIntermediateBand(zoom: number): boolean {
  return lodBandForZoom(zoom) === "intermediate";
}

export function isOverviewBand(zoom: number): boolean {
  return lodBandForZoom(zoom) === "overview";
}

/** True once the camera is at or past the intermediate band (content becomes
 * discoverable — publication/artwork/snapshot fetches may begin). Overview stays
 * pure geography + brand image. */
export function contentDiscoverable(zoom: number): boolean {
  return zoom > OVERVIEW_MAX_ZOOM;
}
